import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { endpoints, formatApiError } from "../lib/api";
import { formatCurrency, SITE_CONDITION_TYPES } from "../lib/romEngine";
import { DEMO_LEVEL_LABEL } from "./calculator/helpers";
import {
    Brain, Sparkle, Crown, ArrowsClockwise, Lightning, CaretRight, ChartLineUp, Cpu,
} from "@phosphor-icons/react";

export default function Calibration() {
    const { user } = useAuth();
    const isManager = user?.role === "manager" || user?.role === "admin";
    const [data, setData] = useState(null);
    const [ml, setMl] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => { reload(); }, []);
    async function reload() {
        try {
            const { data } = await endpoints.calibrationAnalysis();
            setData(data);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e));
        }
        try {
            const { data: m } = await endpoints.mlStatus();
            setMl(m);
        } catch { /* shadow layer optional */ }
    }

    async function toggleGold(u) {
        try {
            await endpoints.setUserGold(u.user_id, { gold_standard: !u.gold_standard });
            await reload();
        } catch (e) {
            setMsg(formatApiError(e));
        }
    }

    async function applyTuning() {
        if (!data?.proposal?.labour_hours_per_sqft) return;
        setBusy(true);
        try {
            const { data: r } = await endpoints.applyTuning({
                labour_hours_per_sqft: data.proposal.labour_hours_per_sqft,
                note: `Auto-tuned from ${data.proposal.rationale.length} loss-type signal(s) using gold-standard estimators`,
            });
            setMsg(`Calibrated · new rate schedule v${r.version} is now active`);
            await reload();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
        setTimeout(() => setMsg(""), 5000);
    }

    async function applySupervisionTuning() {
        if (!data?.supervision_proposal?.supervision_pct) return;
        setBusy(true);
        try {
            const { data: r } = await endpoints.applyTuning({
                supervision_pct: data.supervision_proposal.supervision_pct,
                note: `Auto-tuned supervision_pct from ${data.supervision_proposal.rationale.length} loss-type signal(s) using gold-standard estimators`,
            });
            setMsg(`Calibrated · supervision · new rate schedule v${r.version} is now active`);
            await reload();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
        setTimeout(() => setMsg(""), 5000);
    }

    if (!data) {
        return (
            <div className="max-w-[1600px] mx-auto px-6 py-8 text-[var(--rom-text-2)]">
                {err || "Loading calibration analysis…"}
            </div>
        );
    }

    const hasProposal = data.proposal?.rationale?.length > 0;
    const hasSupProposal = data.supervision_proposal?.rationale?.length > 0;

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        Calibration loop · Coefficient Tuner
                    </div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Brain size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        ROM vs Actual · auto-tune the engine
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-2 max-w-2xl leading-relaxed">
                        Tune sq-ft coefficients toward your <b className="text-[var(--rom-orange)]">gold-standard estimators</b> — not the average. The
                        engine watches every closed job and proposes adjustments only when there&apos;s enough signal from your best people.
                    </p>
                </div>
                <button
                    onClick={reload}
                    className="btn btn-ghost"
                    data-testid="calib-refresh"
                >
                    <ArrowsClockwise size={14} weight="bold" /> Refresh
                </button>
            </header>

            <div className="rounded-[4px] bg-[var(--rom-orange-soft)]/50 border border-[var(--rom-orange)]/40 px-4 py-3 text-xs text-[var(--rom-orange-dark)] flex items-start gap-2" data-testid="residential-calibration-flag">
                <Brain size={15} weight="bold" className="mt-0.5 shrink-0" />
                <span>
                    <b>Residential scale is uncalibrated.</b> All validated jobs to date (Mequon, Mitre Box, Ebenezer) are commercial / large-loss. Residential ROMs run on <b>schedule-default</b> coefficients and the wide ±25% starting band until residential actuals are recorded — don&apos;t read residential output as calibrated yet. Coefficients are tuned per <b>(loss type × property scale)</b>, so a residential actual never moves the commercial bucket.
                </span>
            </div>

            {(msg || err) && (
                <div className={`p-3 rounded-[4px] text-sm border ${err ? "bg-red-50 border-red-200 text-red-700" : "bg-[var(--rom-green-soft)] border-[#bce3cb] text-[var(--rom-green)]"}`} data-testid="calib-msg">
                    {err || msg}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Closed jobs (with actuals)" value={data.total_closed_jobs} accent="navy" testId="stat-closed" />
                <Stat label="Open jobs" value={data.total_open_jobs} accent="orange" testId="stat-open" />
                <Stat label="Mean Abs Error · all" value={data.mae_pct !== null ? `±${data.mae_pct}%` : "—"} accent="navy" testId="stat-mae" />
                <Stat label="MAE · gold only" value={data.gold_mae_pct !== null ? `±${data.gold_mae_pct}%` : "—"} accent="green" testId="stat-gold-mae" />
            </div>

            {/* ---- Three-point accuracy: engine vs estimator vs actual ---- */}
            <ThreePointPanel tp={data.three_point} threshold={data.volume_threshold} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ---- Estimators ---- */}
                <div className="rom-card lg:col-span-2">
                    <div className="rom-card-head">
                        <h3><Crown size={14} weight="bold" /> Estimators · accuracy &amp; gold standard</h3>
                        {!isManager && <span className="chip chip-gray">View-only · Estimator</span>}
                    </div>
                    {data.estimators.length === 0 ? (
                        <div className="rom-card-body text-sm text-[var(--rom-text-3)]">
                            No closed jobs yet. Record actual T&amp;M costs on jobs to start the calibration loop.
                        </div>
                    ) : (
                        <table className="rom-table">
                            <thead>
                                <tr>
                                    <th>Estimator</th>
                                    <th className="numeric">Closed jobs</th>
                                    <th className="numeric">Bias</th>
                                    <th className="numeric">MAE</th>
                                    <th>Gold standard</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.estimators.map((u) => (
                                    <tr key={u.user_id} data-testid={`estimator-${u.email}`}>
                                        <td>
                                            <div className="font-medium flex items-center gap-2">
                                                {u.name}
                                                {u.gold_standard && <Crown size={14} weight="fill" className="text-[var(--rom-orange)]" />}
                                            </div>
                                            <div className="text-[11px] text-[var(--rom-text-2)] font-mono">{u.email}</div>
                                        </td>
                                        <td className="numeric font-semibold">{u.jobs}</td>
                                        <td className="numeric">{u.bias_pct !== null ? `${u.bias_pct > 0 ? "+" : ""}${u.bias_pct}%` : "—"}</td>
                                        <td className="numeric font-semibold">{u.mae_pct !== null ? `±${u.mae_pct}%` : "—"}</td>
                                        <td>
                                            {isManager ? (
                                                <button
                                                    onClick={() => toggleGold(u)}
                                                    data-testid={`toggle-gold-${u.email}`}
                                                    className={`chip ${u.gold_standard ? "chip-orange" : "chip-gray"}`}
                                                >
                                                    {u.gold_standard ? "★ Gold" : "Mark gold"}
                                                </button>
                                            ) : (
                                                <span className={`chip ${u.gold_standard ? "chip-orange" : "chip-gray"}`}>
                                                    {u.gold_standard ? "★ Gold" : "—"}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ---- By loss type ---- */}
                <div className="rom-card">
                    <div className="rom-card-head"><h3><Lightning size={14} weight="bold" /> Variance by loss type</h3></div>
                    <div className="rom-card-body space-y-3">
                        {data.by_loss_type.length === 0 && (
                            <div className="text-sm text-[var(--rom-text-3)]">No closed jobs grouped yet.</div>
                        )}
                        {data.by_loss_type.map((l) => {
                            const v = l.gold_avg_variance_pct ?? l.avg_variance_pct;
                            const sign = v == null ? "" : (v > 0 ? "+" : "");
                            const color = v == null ? "var(--rom-text-3)" : (Math.abs(v) <= 10 ? "var(--rom-green)" : Math.abs(v) <= 20 ? "var(--rom-orange-dark)" : "var(--rom-danger)");
                            const usedMae = (l.gold_samples >= 2 ? l.gold_mae_pct : l.mae_pct);
                            const bandWhy = l.samples === 0
                                ? "No closed jobs yet — band defaults to ±25% until data arrives."
                                : `Band ±${l.confidence_band_pct}% — from ${l.samples} closed job${l.samples > 1 ? "s" : ""}${l.gold_samples >= 2 ? ` (${l.gold_samples} gold)` : ""}, observed MAE ±${usedMae ?? "—"}%. Widens with little data; tightens toward observed accuracy (floor ±8%) as ${l.samples}/${data.volume_threshold} jobs accumulate.`;
                            return (
                                <div key={l.loss_type} className="border border-[var(--rom-border)] rounded-[4px] p-3" data-testid={`loss-card-${l.loss_type}`}>
                                    <div className="flex items-baseline justify-between mb-1">
                                        <div className="font-semibold uppercase text-xs tracking-wider flex items-center gap-1.5">
                                            {l.loss_type}
                                            <span className={`chip ${l.confidence === "high" ? "chip-green" : "chip-gray"}`} style={{ padding: "0px 5px", fontSize: "9px" }} title={`${l.samples}/${data.volume_threshold} samples`}>
                                                {l.confidence === "high" ? "confident" : "low conf."}
                                            </span>
                                        </div>
                                        <div className="font-mono font-bold" style={{ color }}>
                                            {v == null ? "—" : `${sign}${v}%`}
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-[var(--rom-text-2)] flex justify-between items-center">
                                        <span>{l.samples} total · {l.gold_samples} gold · {l.tier_b_samples} Tier-B</span>
                                        <span
                                            className="chip chip-navy cursor-help"
                                            style={{ padding: "0px 6px", fontSize: "9px" }}
                                            title={bandWhy}
                                            data-testid={`band-chip-${l.loss_type}`}
                                        >
                                            band ±{l.confidence_band_pct}% ⓘ
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ---- Voice/room pricing learning hooks (2026-10) ---- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rom-card" data-testid="variance-by-demo-level-card">
                    <div className="rom-card-head"><h3><Lightning size={14} weight="bold" /> Variance by room demo level</h3></div>
                    <div className="rom-card-body space-y-2">
                        {(!data.by_demo_level || data.by_demo_level.length === 0) && (
                            <div className="text-sm text-[var(--rom-text-3)]">No closed jobs with room-level demo data yet.</div>
                        )}
                        {(data.by_demo_level || []).map((d) => (
                            <div key={d.demo_level} className="flex items-center justify-between border border-[var(--rom-border)] rounded-[4px] p-2.5 text-sm" data-testid={`demo-level-row-${d.demo_level}`}>
                                <span className="font-medium">{DEMO_LEVEL_LABEL[d.demo_level === "none" ? "" : d.demo_level] || d.demo_level}</span>
                                <span className="text-[var(--rom-text-2)] text-xs">{d.samples} job{d.samples === 1 ? "" : "s"}</span>
                                <span className="font-mono font-bold" style={{ color: Math.abs(d.avg_variance_pct) <= 10 ? "var(--rom-green)" : Math.abs(d.avg_variance_pct) <= 20 ? "var(--rom-orange-dark)" : "var(--rom-danger)" }}>
                                    {d.avg_variance_pct > 0 ? "+" : ""}{d.avg_variance_pct}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="rom-card" data-testid="variance-by-site-condition-card">
                    <div className="rom-card-head"><h3><Lightning size={14} weight="bold" /> Variance by site condition</h3></div>
                    <div className="rom-card-body space-y-2">
                        {(!data.by_site_condition || data.by_site_condition.length === 0) && (
                            <div className="text-sm text-[var(--rom-text-3)]">No closed jobs with a priced site condition yet.</div>
                        )}
                        {(data.by_site_condition || []).map((d) => {
                            const label = SITE_CONDITION_TYPES.find((t) => t.v === d.condition_type)?.label || d.condition_type;
                            return (
                                <div key={d.condition_type} className="flex items-center justify-between border border-[var(--rom-border)] rounded-[4px] p-2.5 text-sm" data-testid={`site-condition-variance-row-${d.condition_type}`}>
                                    <span className="font-medium">{label}</span>
                                    <span className="text-[var(--rom-text-2)] text-xs">{d.samples} job{d.samples === 1 ? "" : "s"}</span>
                                    <span className="font-mono font-bold" style={{ color: Math.abs(d.avg_variance_pct) <= 10 ? "var(--rom-green)" : Math.abs(d.avg_variance_pct) <= 20 ? "var(--rom-orange-dark)" : "var(--rom-danger)" }}>
                                        {d.avg_variance_pct > 0 ? "+" : ""}{d.avg_variance_pct}%
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ---- Proposal ---- */}
            <div className="rom-card border-t-4 border-t-[var(--rom-orange)]">
                <div className="rom-card-head">
                    <h3>
                        <Sparkle size={14} weight="fill" className="text-[var(--rom-orange)]" />
                        Proposed tuning · labour-hours per sq ft
                    </h3>
                    {hasProposal && isManager && (
                        <button
                            onClick={applyTuning}
                            className="btn btn-primary"
                            disabled={busy}
                            data-testid="apply-tuning"
                        >
                            {busy ? "Applying…" : `Apply & create v${(data.active_schedule_version || 0) + 1}`}
                            <CaretRight size={14} weight="bold" />
                        </button>
                    )}
                </div>
                <div className="rom-card-body">
                    {!hasProposal ? (
                        <div className="text-sm text-[var(--rom-text-2)]">
                            <p className="mb-2">Not enough signal yet to propose a change. The engine waits for:</p>
                            <ul className="list-disc ml-6 space-y-1 text-[var(--rom-text-2)]">
                                <li>At least one estimator marked <b>★ Gold</b></li>
                                <li>At least <b>2 closed gold-standard jobs</b> per loss type</li>
                            </ul>
                            <p className="mt-3">Today: {data.estimators.filter(e => e.gold_standard).length} gold estimator(s), {data.total_closed_jobs} closed job(s) total.</p>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-[var(--rom-text-2)] mb-4 max-w-3xl">
                                Based on your <b>gold-standard estimators&apos;</b> closed jobs vs the ROMs they produced. Adjustments are
                                damped to ±25% per cycle so the engine drifts toward truth instead of swinging.
                            </p>
                            <table className="rom-table">
                                <thead>
                                    <tr>
                                        <th>Loss type</th>
                                        <th className="numeric">Current coefficient</th>
                                        <th className="numeric">Proposed</th>
                                        <th className="numeric">Δ</th>
                                        <th className="numeric">Gold samples</th>
                                        <th>Rationale</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.proposal.rationale.map((r) => {
                                        const delta = ((r.proposed - r.current) / r.current * 100);
                                        return (
                                            <tr key={r.loss_type}>
                                                <td className="capitalize font-medium">{r.loss_type}</td>
                                                <td className="numeric">{r.current}</td>
                                                <td className="numeric font-bold text-[var(--rom-orange-dark)]">{r.proposed}</td>
                                                <td className="numeric"><span className={delta > 0 ? "text-amber-700" : "text-[var(--rom-green)]"}>{delta > 0 ? "+" : ""}{delta.toFixed(1)}%</span></td>
                                                <td className="numeric">{r.gold_samples}</td>
                                                <td className="text-xs text-[var(--rom-text-2)]">{r.note}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            </div>

            {/* ---- Supervision proposal (independent of labour-hours) ---- */}
            <div className="rom-card border-t-4 border-t-[var(--rom-navy)]">
                <div className="rom-card-head">
                    <h3>
                        <Sparkle size={14} weight="fill" className="text-[var(--rom-navy)]" />
                        Proposed tuning · supervision % of field labour
                    </h3>
                    {hasSupProposal && isManager && (
                        <button
                            onClick={applySupervisionTuning}
                            className="btn btn-primary"
                            disabled={busy}
                            data-testid="apply-supervision-tuning"
                        >
                            {busy ? "Applying…" : `Apply supervision & create v${(data.active_schedule_version || 0) + 1}`}
                            <CaretRight size={14} weight="bold" />
                        </button>
                    )}
                </div>
                <div className="rom-card-body">
                    {!hasSupProposal ? (
                        <div className="text-sm text-[var(--rom-text-2)]">
                            <p className="mb-2">No supervision signal yet. This tunes independently of labour-hours and needs:</p>
                            <ul className="list-disc ml-6 space-y-1">
                                <li>Closed <b>Tier-B</b> gold jobs that capture <b>PM / supervision</b> as a separate actuals line (not bundled into field labour)</li>
                                <li>At least <b>2</b> such jobs per loss type</li>
                            </ul>
                        </div>
                    ) : (
                        <table className="rom-table">
                            <thead>
                                <tr>
                                    <th>Loss type</th>
                                    <th className="numeric">Current %</th>
                                    <th className="numeric">Proposed %</th>
                                    <th className="numeric">Gold samples</th>
                                    <th>Rationale</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.supervision_proposal.rationale.map((r) => (
                                    <tr key={r.loss_type}>
                                        <td className="capitalize font-medium">{r.loss_type}</td>
                                        <td className="numeric">{(r.current * 100).toFixed(1)}%</td>
                                        <td className="numeric font-bold text-[var(--rom-navy)]">{(r.proposed * 100).toFixed(1)}%</td>
                                        <td className="numeric">{r.gold_samples}</td>
                                        <td className="text-xs text-[var(--rom-text-2)]">{r.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>


            {/* ---- Shadow ML layer ---- */}
            <MLShadowPanel ml={ml} />
        </div>
    );
}

function ThreePointPanel({ tp, threshold }) {
    if (!tp) return null;
    const cell = (v) => (v == null ? "—" : `±${v}%`);
    return (
        <div className="rom-card" data-testid="three-point-panel">
            <div className="rom-card-head">
                <h3><ChartLineUp size={14} weight="bold" /> Three-point accuracy · engine → estimator → actual</h3>
            </div>
            <div className="rom-card-body">
                <p className="text-sm text-[var(--rom-text-2)] mb-4 max-w-3xl leading-relaxed">
                    The raw coefficient model (<b>engine</b>), the estimator&apos;s edited ROM (<b>estimator</b>), and the
                    final invoice (<b>actual</b>) are stored as distinct points per job. This shows whether Brandon&apos;s
                    judgement is improving on the engine — and by how much.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MiniStat label="Engine MAE vs actual" value={cell(tp.engine_mae_pct)} sub={`${tp.jobs_with_engine_prediction} jobs w/ engine point`} />
                    <MiniStat label="Estimator MAE vs actual" value={cell(tp.estimator_mae_pct)} sub="after Section A/equipment edits" highlight />
                    <MiniStat label="Estimator improved" value={`${tp.estimator_improved_count} / ${tp.jobs_with_engine_prediction}`} sub="jobs where edits beat engine" />
                    <MiniStat label="Tier A · Tier B" value={`${tp.tier_a_count} · ${tp.tier_b_count}`} sub={`Tier B trains ML (≥${threshold})`} />
                </div>
                {tp.jobs_with_engine_prediction === 0 && (
                    <div className="mt-4 text-[11px] text-[var(--rom-text-3)] bg-slate-50 border border-[var(--rom-border)] rounded-[4px] p-3" data-testid="three-point-empty-note">
                        Engine vs estimator separation starts capturing on ROMs saved from now on. Historical
                        calibration-review jobs (Mequon, Mitre Box, Ebenezer) predate the three-point store, so they
                        feed total-level variance only.
                    </div>
                )}
            </div>
        </div>
    );
}

function MLShadowPanel({ ml }) {
    if (!ml) return null;
    return (
        <div className="rom-card border-t-4 border-t-[var(--rom-navy)]" data-testid="ml-shadow-panel">
            <div className="rom-card-head">
                <h3><Cpu size={14} weight="bold" /> Shadow ML layer</h3>
                <span className="chip chip-navy">mode · shadow</span>
            </div>
            <div className="rom-card-body">
                <p className="text-sm text-[var(--rom-text-2)] mb-4 max-w-3xl leading-relaxed">
                    A gradient-boosted model trains per loss type once <b>{ml.threshold}+ Tier-B</b> closed jobs exist, then runs
                    alongside the coefficient engine on every ROM. Its prediction stays hidden until its accuracy beats the
                    engine over the last <b>{ml.rolling_window}</b> jobs. {ml.logged_predictions} prediction(s) logged so far.
                </p>
                <table className="rom-table" data-testid="ml-status-table">
                    <thead>
                        <tr>
                            <th>Loss type</th>
                            <th className="numeric">Tier-B jobs</th>
                            <th className="numeric">Threshold</th>
                            <th>Status</th>
                            <th>Rolling accuracy</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(ml.models || []).map((m) => (
                            <tr key={m.loss_type} data-testid={`ml-row-${m.loss_type}`}>
                                <td className="capitalize font-medium">{m.loss_type}</td>
                                <td className="numeric">{m.tier_b_count}</td>
                                <td className="numeric">{m.threshold}</td>
                                <td>
                                    {m.surfaced ? (
                                        <span className="chip chip-green">Live · beats engine</span>
                                    ) : m.trained ? (
                                        <span className="chip chip-orange">Trained · shadow</span>
                                    ) : (
                                        <span className="chip chip-gray">Collecting data</span>
                                    )}
                                </td>
                                <td className="text-xs text-[var(--rom-text-2)]">
                                    {m.rolling
                                        ? `ML ±${m.rolling.ml_mae} vs engine ±${m.rolling.engine_mae} (n=${m.rolling.window})`
                                        : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MiniStat({ label, value, sub, highlight }) {
    return (
        <div className={`p-3 rounded-[4px] border ${highlight ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]" : "border-[var(--rom-border)]"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            <div className="font-display font-black text-xl mt-1 tabular">{value}</div>
            <div className="text-[10px] text-[var(--rom-text-2)] mt-0.5">{sub}</div>
        </div>
    );
}

function Stat({ label, value, accent, testId }) {
    const color = { orange: "var(--rom-orange)", navy: "var(--rom-navy)", green: "var(--rom-green)" }[accent] || "var(--rom-navy)";
    return (
        <div className="rom-card" data-testid={testId}>
            <div className="rom-card-body">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">{label}</div>
                <div className="mt-2 font-display font-black text-3xl tabular tracking-tight" style={{ color }}>
                    {value ?? "—"}
                </div>
            </div>
        </div>
    );
}
